import type {
  InitiateUploadParams,
  InitiateUploadResponse,
  SingleUploadData,
  UploadPurpose,
} from "../app/services/uploadApi";

/**
 * `POST /api/upload/register` を経由しない purpose 向けの S3 アップロード。
 *
 * `note` は URL を Markdown 記法で本文へ、`tool_asset` /
 * `user_asset` は URL・キーを保存先レコードへ直接持たせるため、register を
 * 通さず initiate → presigned POST の 2 ステップで完結する。
 *
 * multipart になるサイズは扱わない（いずれも数 MB 以下の画像・添付が対象）。
 * multipart が必要な用途は `useS3Upload` を使う。
 */

/** presigned POST のバケット URL と file_key から公開 URL を組み立てる */
export const buildS3ObjectUrl = (baseUrl: string, fileKey: string): string => {
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const encodedKey = fileKey
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `${base}${encodedKey}`;
};

export interface UploadedFile {
  /** S3 オブジェクトキー */
  fileKey: string;
  /** Markdown 記法へ埋め込む公開 URL */
  url: string;
}

/** `initiateUpload` mutation trigger（`.unwrap()` 可能なもの）の最小形 */
// eslint-disable-next-line no-unused-vars
export type InitiateUploadTrigger = (params: InitiateUploadParams) => {
  unwrap: () => Promise<InitiateUploadResponse>;
};

/**
 * ファイル 1 件を S3 へアップロードし、公開 URL を返す。
 *
 * @param initiate uploadApi の initiateUpload mutation trigger
 * @param file アップロードするファイル
 * @param options purpose と、その purpose が要求するスコープ（project_id 等）
 */
export const uploadFileDirect = async (
  initiate: InitiateUploadTrigger,
  file: File,
  options: {
    purpose: UploadPurpose;
    projectId?: string;
    /** 上限超過時のエラーメッセージ（用途ごとの言い回しに合わせる） */
    tooLargeMessage?: string;
  },
): Promise<UploadedFile> => {
  const result = await initiate({
    file_name: file.name,
    file_size: file.size,
    content_type: file.type || "application/octet-stream",
    purpose: options.purpose,
    ...(options.projectId ? { project_id: options.projectId } : {}),
  }).unwrap();

  const data = result.data as SingleUploadData;
  if (data.upload_type !== "single" || !data.presigned_post) {
    throw new Error(
      options.tooLargeMessage ??
        "添付できるファイルは 50MB 未満のみです",
    );
  }

  const formData = new FormData();
  Object.entries(data.presigned_post.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  const response = await fetch(data.presigned_post.url, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`ファイルのアップロードに失敗しました（${response.status}）`);
  }

  return {
    fileKey: data.file_key,
    url: buildS3ObjectUrl(data.presigned_post.url, data.file_key),
  };
};
